import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileDown } from 'lucide-react'
import { kurzHash, type SiteDocPublicCheck } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { publicCheck, publicCheckPdfUrl } from '../api/site-docs.api'

/**
 * AP-46 23. — **Prüfseite** hinter dem QR-Code, ohne Anmeldung: Art, Nummer, Datum, Betrieb, Zustand, Prüfwert,
 * Ersatz und Nachträge — nicht mehr, als im PDF steht — und „PDF herunterladen". Solange das Dokument noch auf
 * dem Gerät liegt (oder der Code unbekannt ist): „noch nicht angekommen".
 */
export default function PruefenPage() {
  const { t } = useTranslation()
  const { token = '' } = useParams<{ token: string }>()
  const [stand, setStand] = useState<SiteDocPublicCheck | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    publicCheck(token)
      .then(setStand)
      .catch((e) => setFehler(fehlertext(e, t)))
  }, [token, t])

  const zeile = (label: string, wert: string | null) =>
    wert ? (
      <div className="flex justify-between gap-3 border-b py-1.5 last:border-0">
        <dt className="text-muted-foreground">{label}</dt>
        <dd className="text-right font-medium">{wert}</dd>
      </div>
    ) : null

  return (
    <div className="min-h-screen bg-background px-4 py-6 text-foreground">
      <main className="mx-auto max-w-md space-y-4">
        <h1 className="text-2xl font-bold">{t('siteDocs.public.checkTitle')}</h1>
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
        {!stand && !fehler && <p className="text-muted-foreground">{t('common.loading')}</p>}
        {stand && !stand.found && <p>{t('siteDocs.public.notArrived')}</p>}
        {stand?.found && (
          <>
            <dl className="rounded-md border px-3 text-sm">
              {zeile(t('siteDocs.public.company'), stand.tenantName)}
              {zeile(stand.kind ? t(`siteDocs.kinds.${stand.kind}`) : '', stand.number)}
              {zeile(t('siteDocs.preview.date'), stand.documentDate ? stand.documentDate.split('-').reverse().join('.') : null)}
              {zeile(t('siteDocs.columns.status'), stand.status ? t(`siteDocs.public.states.${stand.status}`) : null)}
              {zeile(t('siteDocs.release.checkValue'), stand.contentHash ? kurzHash(stand.contentHash) : null)}
              {zeile(t('siteDocs.public.supersededBy'), stand.supersededBy)}
              {zeile(t('siteDocs.public.supplements'), stand.supplements.length ? stand.supplements.join(', ') : null)}
            </dl>
            {stand.pdf && (
              <a href={publicCheckPdfUrl(token)} className="inline-flex h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground">
                <FileDown className="h-4 w-4" /> {t('siteDocs.public.downloadPdf')}
              </a>
            )}
          </>
        )}
      </main>
    </div>
  )
}
